/**
 * Formula reference (brief §content: `/library/formulas`). Chapter tabs, each
 * formula rendered as KaTeX with its variables table and notes, and a
 * "Skriv ut / Print" button. This is the print target named in
 * `docs/CONVENTIONS.md`: the chapter switcher and the page chrome are
 * `no-print` (the header actions are already wrapped that way by
 * `<PageHeader>`), and each formula card is `print-plain` (via `<Card>`) so
 * only the active chapter's formulas — not the app shell — end up on paper.
 * The card itself is `@client/components/FormulaCard`, shared with the practice
 * session's formula panel.
 */
import { useState } from "react";
import clsx from "clsx";
import { FormulaCard } from "@client/components/FormulaCard";
import { Button, EmptyState, PageHeader, Spinner } from "@client/components/ui";
import { useTopicsAndChapters } from "@client/api/topics";
import { useT } from "@client/i18n";
import { messageForError } from "@client/lib/errors";
import { useFormulas } from "./api";

export function FormulasPage() {
  const t = useT();
  const formulasQuery = useFormulas();
  // The chapter tabs come from the taxonomy, never from a hardcoded 1…8 —
  // chapter 9 ("Statistisk mekanik") joined the course after the first pass.
  const topicsQuery = useTopicsAndChapters();
  const chapters = (topicsQuery.data?.chapters ?? []).map((c) => c.number);
  const [chapter, setChapter] = useState(1);

  const activeFormulas = (formulasQuery.data?.formulas ?? []).filter((f) => f.chapter === chapter);

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title={t("content.nav.formulas")}
        description={t("content.formulas.description")}
        actions={
          <Button variant="secondary" size="sm" onClick={() => window.print()}>
            {t("content.formulas.print")}
          </Button>
        }
      />

      <div
        role="tablist"
        aria-label={t("content.nav.formulas")}
        className="no-print mb-6 flex flex-wrap gap-1 border-b border-ink-200 dark:border-ink-800"
      >
        {chapters.map((number) => (
          <button
            key={number}
            type="button"
            role="tab"
            aria-selected={chapter === number}
            onClick={() => setChapter(number)}
            className={clsx(
              "-mb-px rounded-t-lg border-b-2 px-3 py-2 text-sm font-medium transition-colors",
              chapter === number
                ? "border-accent-600 text-accent-800 dark:text-accent-200"
                : "border-transparent text-ink-600 hover:text-ink-900 dark:text-ink-400 dark:hover:text-ink-100",
            )}
          >
            {t("content.formulas.chapterTab", { number })}
          </button>
        ))}
      </div>

      {formulasQuery.isLoading && (
        <div className="flex justify-center py-8">
          <Spinner size="lg" label={t("common.loading")} />
        </div>
      )}

      {formulasQuery.isError && <EmptyState title={messageForError(formulasQuery.error, t)} />}

      <div role="tabpanel">
        {formulasQuery.isSuccess && activeFormulas.length === 0 && (
          <EmptyState title={t("content.formulas.empty")} />
        )}
        {activeFormulas.map((formula) => (
          <FormulaCard key={formula.id} formula={formula} />
        ))}
      </div>
    </div>
  );
}

export default FormulasPage;
